import type { TimelineEventOut, TimelineItemOut } from "@/api";

/**
 * What the record's own timeline says about its handovers (UX gate round 2,
 * #3) — read off the events the record already carries, not off fields of
 * their own: the wire stays as it is.
 *
 * Not who retracted an entry: the entry carries when and why, and its
 * retraction's event is an echo the record leaves out
 * (`workflow.activity.ECHOES`) — the wire does not say who.
 */

function eventsOf(timeline: readonly TimelineItemOut[]): TimelineEventOut[] {
  return timeline.flatMap((item) => (item.kind === "event" ? [item.event] : []));
}

export interface TakenOver {
  /** Who handed the Vorgang over — the person who asked. */
  from: string;
  /** When it was accepted. */
  at: string;
}

/**
 * Whom this Vorgang was last taken over from, and when. The newest
 * acceptance says when; who sent it is the actor of the request before it —
 * one request is open at a time, so the newest request up to the acceptance
 * is the one accepted. The acceptance's own sentence names the sender too,
 * but only as text.
 */
export function takenOver(timeline: readonly TimelineItemOut[]): TakenOver | null {
  const events = eventsOf(timeline);
  const accepted = events.find((event) => event.eventType === "case_handover_accepted");
  if (accepted === undefined) return null;
  const acceptedAt = Date.parse(accepted.at);
  const request = events.find(
    (event) => event.eventType === "case_handover_requested" && Date.parse(event.at) <= acceptedAt,
  );
  const from = request?.actorDisplayName ?? null;
  return from === null ? null : { from, at: accepted.at };
}
